import React, { useRef, useState } from "react";
import { Buildings, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

/**
 * LogoUploader — riutilizzabile su /profilo e /dashboard/agenzia.
 *
 * - `value`: il path/URL salvato in agency_logo_url
 * - `onChange(newUrl)`: chiamato dopo upload/rimozione con il nuovo valore
 * - `onRefresh`: opzionale, refetch del profile dopo cambio
 * - `userId`: per costruire l'URL pubblica anche quando il path è interno
 */
export default function LogoUploader({ value, onChange, onRefresh, userId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewSrc = (() => {
    if (!value) return null;
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    if (userId) return `${API}/public/agency/${userId}/logo?ts=${Date.now()}`;
    return null;
  })();

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Il file è troppo grande (max 2 MB).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/agency/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(data.agency_logo_url || "");
      toast.success("Logo caricato");
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRemove = () => {
    toast(
      "Rimuovere il logo dell'agenzia?",
      {
        action: {
          label: "Rimuovi",
          onClick: async () => {
            try {
              await api.delete("/profile/agency/logo");
              onChange?.("");
              toast.success("Logo rimosso");
              onRefresh?.();
            } catch { toast.error("Errore rimozione"); }
          },
        },
        cancel: { label: "Annulla", onClick: () => {} },
        duration: 8000,
      },
    );
  };

  return (
    <div className="flex items-center gap-4" data-testid="logo-uploader">
      <div className="w-24 h-24 flex-shrink-0 border border-[#E5E7E0] bg-[#F7F7F3] flex items-center justify-center overflow-hidden rounded-lg">
        {previewSrc ? (
          <img
            src={previewSrc}
            alt="Logo agenzia"
            className="w-full h-full object-contain p-2"
            data-testid="logo-preview"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <Buildings size={28} weight="light" className="text-[#C3C2BD]" />
        )}
      </div>
      <div className="flex-1 flex items-center gap-2 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={onFile}
          className="hidden"
          data-testid="logo-file-input"
        />
        <button
          type="button"
          onClick={onPick}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#7C8B6E] hover:text-white rounded-lg px-4 py-2 text-[12.5px] font-semibold transition-colors disabled:opacity-60"
          data-testid="logo-upload-btn"
        >
          <UploadSimple size={14} weight="bold" />
          {uploading ? "Caricamento..." : value ? "Sostituisci logo" : "Carica logo"}
        </button>
        {value && (
          <button
            type="button"
            onClick={onRemove}
            className="text-[12px] text-[#6B6A65] hover:text-rose-500 flex items-center gap-1"
            data-testid="logo-remove-btn"
          >
            <Trash size={13} /> Rimuovi
          </button>
        )}
      </div>
    </div>
  );
}
